// =============================================================================
// BUDGET CATEGORY PROGRESS - Barres horizontales par catégorie
// =============================================================================

import { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { PieChart } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatMontant } from '@/lib/formatters';
import type { BudgetCategoryComparison, BudgetCategory } from '@/types/budgetVentile.types';
import {
  CATEGORIES_BUDGET,
  getCategoryStatus,
} from '@/types/budgetVentile.types';
import { CHART_STATUS_COLORS } from './chartConfig';

// =============================================================================
// PROPS
// =============================================================================

interface BudgetCategoryProgressProps {
  categories: Record<BudgetCategory, BudgetCategoryComparison>;
  onEdit: () => void;
}

// =============================================================================
// COMPOSANT CARD CATÉGORIE
// =============================================================================

function CategoryCard({
  icon,
  label,
  data,
}: {
  icon: string;
  label: string;
  data: BudgetCategoryComparison;
}) {
  const status = getCategoryStatus(data.pourcentage);
  const pct = Math.min(data.pourcentage, 100);
  const isOver = data.pourcentage > 100;

  // Couleur selon le statut
  const statusColor = status === 'critique'
    ? CHART_STATUS_COLORS.deficit
    : status === 'attention'
    ? CHART_STATUS_COLORS.attention
    : CHART_STATUS_COLORS.excellent;

  return (
    <div className="relative p-4 rounded-xl bg-card border border-border/50 hover:border-border transition-colors">
      {/* Header avec icône et label */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-lg">{icon}</span>
          <span className="text-sm font-medium text-foreground">{label}</span>
        </div>
        {/* Badge pourcentage */}
        <span
          className={cn(
            "px-2 py-0.5 rounded-full text-xs font-bold",
            status === 'critique' && "bg-red-500/15 text-red-500",
            status === 'attention' && "bg-amber-500/15 text-amber-500",
            status === 'confortable' && "bg-emerald-500/15 text-emerald-500"
          )}
        >
          {data.pourcentage.toFixed(0)}%
        </span>
      </div>

      {/* Barre de progression horizontale */}
      <div className="relative mb-3">
        {/* Fond avec zones */}
        <div className="h-3 rounded-full overflow-hidden bg-muted/50 flex">
          <div className="h-full" style={{ width: '70%', backgroundColor: `${CHART_STATUS_COLORS.excellent}15` }} />
          <div className="h-full" style={{ width: '20%', backgroundColor: `${CHART_STATUS_COLORS.attention}15` }} />
          <div className="h-full" style={{ width: '10%', backgroundColor: `${CHART_STATUS_COLORS.deficit}15` }} />
        </div>

        {/* Barre de progression remplie */}
        <div
          className="absolute top-0 left-0 h-3 rounded-full transition-all duration-700 ease-out"
          style={{
            width: `${pct}%`,
            background: `linear-gradient(90deg, ${CHART_STATUS_COLORS.excellent} 0%, ${statusColor} 100%)`,
            boxShadow: `0 0 8px ${statusColor}40`,
          }}
        />

        {/* Marqueurs de zones */}
        <div className="absolute top-0 left-[70%] h-3 w-px bg-border/50" />
        <div className="absolute top-0 left-[90%] h-3 w-px bg-border/50" />
      </div>

      {/* Montants */}
      <div className="flex justify-between items-baseline">
        <div className="space-y-0.5">
          <span className="text-xs text-muted-foreground block">Réalisé</span>
          <span className="text-sm font-semibold text-foreground">
            {formatMontant(data.reel)}
          </span>
        </div>
        <div className="text-right space-y-0.5">
          <span className="text-xs text-muted-foreground block">Prévu</span>
          <span className="text-sm text-muted-foreground">
            {formatMontant(data.prevu)}
          </span>
        </div>
      </div>

      {/* Indicateur de reste ou dépassement */}
      <div className={cn(
        "mt-3 pt-2 border-t border-border/30 flex items-center justify-center gap-1",
      )}>
        {isOver ? (
          <>
            <span className="text-xs text-muted-foreground">Dépassement :</span>
            <span className="text-xs font-semibold text-red-500">
              +{formatMontant(Math.abs(data.ecart))}
            </span>
          </>
        ) : (
          <>
            <span className="text-xs text-muted-foreground">Excédent :</span>
            <span className="text-xs font-semibold text-emerald-500">
              {formatMontant(data.ecart)}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================

export function BudgetCategoryProgress({
  categories,
  onEdit,
}: BudgetCategoryProgressProps) {
  // Filtrer les catégories avec budget > 0
  const activeCategories = useMemo(() => {
    return CATEGORIES_BUDGET.filter((cat) => categories[cat.key].prevu > 0);
  }, [categories]);

  // Calculer les totaux
  const totals = useMemo(() => {
    const prevu = Object.values(categories).reduce((sum, c) => sum + c.prevu, 0);
    const reel = Object.values(categories).reduce((sum, c) => sum + c.reel, 0);
    return { prevu, reel, ecart: prevu - reel };
  }, [categories]);

  return (
    <div className="mt-10 pt-8 border-t-2 border-border space-y-4">
      {/* En-tête - même style que "Consommation budget" */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Détail par poste de dépense</h3>
        <Button
          size="sm"
          onClick={onEdit}
          className="h-8 gap-2 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
        >
          <PieChart className="h-3.5 w-3.5" />
          Modifier Budget
        </Button>
      </div>

      {/* Grille des catégories */}
      <div className="grid grid-cols-2 gap-3">
        {activeCategories.map((cat) => (
          <CategoryCard
            key={cat.key}
            icon={cat.icon}
            label={cat.label}
            data={categories[cat.key]}
          />
        ))}
      </div>

      {/* Résumé total */}
      <div className="flex items-center justify-between px-4 py-3 rounded-lg bg-muted/30 border border-border/50">
        <span className="text-xs text-muted-foreground">Total ventilé</span>
        <div className="flex items-center gap-4">
          {/* Dépensé */}
          <div className="text-center">
            <span className="text-sm font-semibold text-foreground block">
              {formatMontant(totals.reel)}
            </span>
            <span className="text-[10px] text-muted-foreground">Réalisé</span>
          </div>

          <span className="text-muted-foreground/50">/</span>

          {/* Budget prévu */}
          <div className="text-center">
            <span className="text-sm text-muted-foreground block">
              {formatMontant(totals.prevu)}
            </span>
            <span className="text-[10px] text-muted-foreground">Prévu</span>
          </div>

          {/* Reste/Dépassement */}
          {totals.ecart !== 0 && (
            <div className="text-center">
              <span className={cn(
                "text-sm font-medium block",
                totals.ecart >= 0 ? "text-emerald-500" : "text-red-500"
              )}>
                {totals.ecart >= 0 ? '+' : ''}{formatMontant(totals.ecart)}
              </span>
              <span className="text-[10px] text-muted-foreground">
                {totals.ecart >= 0 ? 'Excédent' : 'Dépassement'}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
